import { exists, formatDurationInHHMM, convertTimeInMinutes } from '../../../../common/utilities/utilitiesFunctions';
import { hasTransfer } from '../../../../common/utilities/shiftFunctions';
import moment from 'moment';
import { sortBy } from 'lodash';
import { UkgDateFormat } from '../../../Enums';
import DateFormatService from '../../../services/DateFormatService';
import i18n from '../../../../i18n';

const tForEver = i18n.t('workplanManager:workPlanView.global.forever');

export const formatShiftLinesToSave = (selectedWorkPlan) => {
    let shiftLines = selectedWorkPlan.shiftLines.filter(shiftLine => shiftLine.editMode)
    let customShiftLines = [];

    for (const shiftLine of shiftLines) {
        if (shiftLine.type !== 'relief') {
            let shiftlineEnddate = shiftLine.effectiveDate && !shiftLine.endDate ? selectedWorkPlan.endDate : shiftLine.endDate;
            let customShiftLine = Object.assign({}, {
                Job: (shiftLine.job === '' || shiftLine.job === null) ? {} : { NodeId: shiftLine.job.value, OrgPath: shiftLine.job.label, ParentNodeId: shiftLine.job.parentNodeId },
                SchedulePatternName: shiftLine.schedulePatternName,
                Details: [],
                IsNew: shiftLine.IsNew,
                MarkedForDeletion: shiftLine.markedForDeletion,
                ShiftLineId: typeof shiftLine.shiftLineId === "string" ? null : shiftLine.shiftLineId,
                EmployeeId: shiftLine.employeeId === '' ? null : shiftLine.employeeId,
                EmployeeChanged: shiftLine.EmployeeChanged,
                EmployeeName: shiftLine.employeeFullName,
                StartDate: shiftLine.effectiveDate,
                EndDate: evalForeverStringToDate(shiftlineEnddate)
            });

            for (let i = 1; i <= selectedWorkPlan.nbDays; i++) {
                if (exists(shiftLine['day' + i])) {
                    for (const item of shiftLine['day' + i]) {
                        if (exists(item.kronosShiftTemplate)) {
                            customShiftLine.Details.push({
                                day: i,
                                ShiftTemplate: {
                                    Id: item.kronosShiftTemplate.id,
                                    Name: item.name,
                                    Description: 'desc',
                                    StartDateTime: item.startDateTime,
                                    EndDateTime: item.endDateTime,
                                    Label: item.label,
                                    Segments: item.kronosShiftTemplate.segments
                                }
                            })
                        } if (exists(item.segments)) {
                            customShiftLine.Details.push({
                                day: i,
                                ShiftTemplate: {
                                    Id: item.id,
                                    Name: item.name,
                                    Description: 'desc',
                                    StartDateTime: item.startDateTime,
                                    EndDateTime: item.endDateTime,
                                    Label: item.label,
                                    Segments: item.segments
                                }
                            })
                        } else if (exists(item.durationInTime)) {
                            customShiftLine.Details.push({
                                day: i,
                                PayCodeTemplate: {
                                    PayCode: { id: item.id },
                                    Id: item.id,
                                    Name: item.name,
                                    DurationInTime: typeof (item.durationInTime) === 'string' ? parseInt(convertTimeInMinutes(item.durationInTime)) : item.durationInTime,
                                    StartDateTime: item.startTime,
                                }
                            })
                        }
                    }
                }
            }
            customShiftLines.push(customShiftLine);
        }
    }
    return customShiftLines;
}

export const genData = (workplan, workplanSettings) => {
    let reliefs = [];

    for (let [index, shiftLine] of workplan.shiftLines.entries()) {
        shiftLine.lineId = index;
        shiftLine.orgPath = shiftLine.orgPath === null ? '' : shiftLine.orgPath;
        shiftLine.job = shiftLine.job === '' ? '' : (exists(shiftLine.job.value) ? shiftLine.job : { value: shiftLine.job.nodeId, label: shiftLine.job.orgPath, parentNodeId: shiftLine.job.parentNodeId });
        shiftLine.type = 'item';
        shiftLine.EmployeeChanged = false;
        shiftLine.employeeId = shiftLine.employeeId === null ? '' : shiftLine.employeeId;
        shiftLine.effectiveDate = shiftLine.effectiveDate ? shiftLine.effectiveDate : null;
        shiftLine.endDate = shiftLine.endDate ? shiftLine.endDate : null;
        shiftLine.editMode = false;
        shiftLine.checked = false;

        let currentState = shiftLine.states.find(status => status.isCurrent);

        shiftLine.currentStatus = currentState
            ? { ...currentState, effectiveDate: currentState.effectiveDate, endDate: currentState.endDate }
            : { ...shiftLine.currentStatus, effectiveDate: 0, endDate: 0 };

        for (let i = 1; i <= workplan.nbDays; i++) {
            shiftLine['day' + i] = [];
        }

        for (const shift of shiftLine.shiftTemplates) {
            shiftLine['day' + shift.day].push(shift);
        }

        for (const paycode of shiftLine.paycodeTemplates) {
            shiftLine['day' + paycode.day].push({ ...paycode, duration: formatDurationInHHMM(paycode.durationInTime) });
        }

        shiftLine.maxItemsByDay = getShiftLineHeight(shiftLine, workplan);

        if (shiftLine.reliefs.length !== 0 || shiftLine.owners.length !== 0) {
            reliefs = [...reliefs, ...shiftLine.summaryReliefs.map(relief => {
                return {
                    ...relief,
                    schedulePatternName: shiftLine.schedulePatternName,
                    parentId: shiftLine.shiftLineId,
                    type: 'relief',
                    shiftLineId: 'relief',
                    shiftLineEntityId: shiftLine.shiftLineId
                }
            })];
        }
    }

    workplan.shiftLines = [...workplan.shiftLines, ...reliefs];

    let enableSystemCoverage = false;
    if (workplanSettings.content !== undefined && workplanSettings.content.coverage !== undefined){
        enableSystemCoverage = workplanSettings.content.coverage.enableSystemCoverage;
    }
    workplan.quotas = genQuotas(workplan, enableSystemCoverage);
    const tableData = workplan.shiftLines.map((shiftline) =>
    ({
        ...shiftline,
        id: shiftline.lineId,
        children: reliefs
            .filter((x) => x.parentId === shiftline.shiftLineId)
            .map((x, i) => ({
                ...x,
                id: `${shiftline.lineId}-${i}`
            }))
    }));
    workplan.shiftLines = tableData;
    workplan.quotas = genQuotas(workplan);
}

export const genQuotas = (workplan, enableSystemCoverage = false) => {
    let quotasObject = {};
    const { jobs, nbDays, shiftLines, systemCoverage } = workplan;

    for (const job of jobs) {
        if (!exists(quotasObject[job.value])) {
            quotasObject[job.value] = {
                label: job.label,
                shiftLineId: 'job' + job.value,
                sort: job.value.toString()
            };
        }
        for (let i = 1; i <= nbDays; i++) {

            if (!exists(quotasObject[job.value]['day' + i])) {
                quotasObject[job.value]['day' + i] = { part1: 0, part2: 0, part3: 0 };
            }

            for (const shiftLine of shiftLines) {
                let shiftLineIsAssigned = (exists(shiftLine.owners) && shiftLine.currentOwner !== null) || (exists(shiftLine.reliefs) && shiftLine.currentRelief !== null);
                if (shiftLine.job !== null && exists(shiftLine.job) && job.value === shiftLine.job.value && !exists(shiftLine.parentId)) {
                    for (const item of shiftLine['day' + i]) {

                        if (exists(item.kronosShiftTemplate) || exists(item.segments)) {
                            let itemTransfer;

                            if (item.kronosShiftTemplate) {
                                itemTransfer = item.kronosShiftTemplate.segments.find(x => x.transferString !== null && x.transferString !== '' && x.transferString !== undefined && x.orgNode !== null);
                            } else {
                                itemTransfer = item.segments.find(x => x.transferString !== null && x.transferString !== '' && x.transferString !== undefined && x.orgNode !== null);
                            }

                            if (exists(itemTransfer)) {
                                if (!exists(quotasObject[itemTransfer.orgNode.id])) {
                                    quotasObject[itemTransfer.orgNode.id] = {
                                        label: itemTransfer.transferString,
                                        shiftLineId: 'job' + itemTransfer.orgNode.id,
                                        sort: itemTransfer.orgNode.id.toString()
                                    };
                                }
                                if (!exists(quotasObject[itemTransfer.orgNode.id]['day' + i])) {
                                    quotasObject[itemTransfer.orgNode.id]['day' + i] = { part1: 0, part2: 0, part3: 0 };
                                }
                                quotasObject[itemTransfer.orgNode.id]['day' + i].part2++;
                            } else {
                                quotasObject[job.value]['day' + i].part2++;
                            }
                            if (shiftLineIsAssigned) {
                                if (exists(itemTransfer)) {
                                    quotasObject[itemTransfer.orgNode.id]['day' + i].part1++;
                                } else {
                                    quotasObject[job.value]['day' + i].part1++;
                                }
                            }

                            let label = '';
                            if (exists(item.kronosShiftTemplate)) {
                                if (item.kronosShiftTemplate.label !== '') {
                                    label = item.kronosShiftTemplate.label + (exists(itemTransfer) ? ' (x)' : '')
                                } else {
                                    label = (item.kronosShiftTemplate.startDateTime.substring(11, 16) + ' - ' + item.kronosShiftTemplate.endDateTime.substring(11, 16) + (hasTransfer(item.kronosShiftTemplate) ? ' (x)' : ''))
                                }
                            } else {
                                if (item.label !== '') {
                                    label = item.label + (hasTransfer(item) ? ' (x)' : '')
                                } else {
                                    label = (item.startDateTime.substring(11, 16) + ' - ' + item.endDateTime.substring(11, 16) + (hasTransfer(item) ? ' (x)' : ''))
                                }
                            }

                            if (!exists(quotasObject[job.value + item.name]) && !exists(itemTransfer)) {
                                quotasObject[job.value + item.name] = {
                                    sort: job.value + item.name,
                                    parentId: 'job' + job.value,
                                    label: label,
                                    shiftLineId: job.value + item.name
                                };
                            }

                            if (!exists(itemTransfer) && !exists(quotasObject[job.value + item.name]['day' + i])) {
                                let workloadFromSystemCoverage = 0;
                                if (enableSystemCoverage) {
                                    const systemCoverageForJob = systemCoverage.find(x => x.jobNodeId === job.value);
                                    const systemCoverageForShift = systemCoverageForJob && systemCoverageForJob.workload[item.label];
                                    const systemCoverageForDay = systemCoverageForShift && systemCoverageForShift.find(x => x.day === i);
                                    workloadFromSystemCoverage = (systemCoverageForDay && systemCoverageForDay.count) || 0;
                                }
                                quotasObject[job.value + item.name]['day' + i] = {
                                    part1: shiftLineIsAssigned ? 1 : 0,
                                    part2: 1,
                                    part3: workloadFromSystemCoverage
                                }
                                quotasObject[job.value]['day' + i].part3 += workloadFromSystemCoverage;
                            } else if (!exists(itemTransfer)) {
                                quotasObject[job.value + item.name]['day' + i].part2++;
                                if (shiftLineIsAssigned) {
                                    quotasObject[job.value + item.name]['day' + i].part1++;
                                }
                            }
                            if (exists(itemTransfer)) {
                                if (!exists(quotasObject[itemTransfer.orgNode.id + item.name])) {
                                    quotasObject[itemTransfer.orgNode.id + item.name] = {
                                        sort: itemTransfer.orgNode.id + item.name,
                                        parentId: 'job' + itemTransfer.orgNode.id,
                                        label: label,
                                        shiftLineId: itemTransfer.orgNode.id + item.name
                                    };
                                }
                            }

                            if ((exists(itemTransfer) && !exists(quotasObject[itemTransfer.orgNode.id + item.name]['day' + i]))) {
                                let workloadFromSystemCoverage = 0;
                                if (enableSystemCoverage) {
                                    const systemCoverageForJob = systemCoverage.find(x => x.jobNodeId === job.value);
                                    const systemCoverageForShift = systemCoverageForJob && systemCoverageForJob.workload[item.label];
                                    const systemCoverageForDay = systemCoverageForShift && systemCoverageForShift.find(x => x.day === i);
                                    workloadFromSystemCoverage = (systemCoverageForDay && systemCoverageForDay.count) || 0;
                                }
                                quotasObject[itemTransfer.orgNode.id + item.name]['day' + i] = {
                                    part1: shiftLineIsAssigned ? 1 : 0,
                                    part2: 1,
                                    part3: workloadFromSystemCoverage
                                }
                                quotasObject[itemTransfer.orgNode.id]['day' + i].part3 += workloadFromSystemCoverage;
                            }
                            else if (exists(itemTransfer)) {
                                quotasObject[itemTransfer.orgNode.id + item.name]['day' + i].part2++;
                                if (shiftLineIsAssigned) {
                                    quotasObject[itemTransfer.orgNode.id + item.name]['day' + i].part1++;
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    let quotas = [];
    let quotaObjectsList = Object.values(quotasObject);
    let quotasObjectChildren = quotaObjectsList.filter(x => x.parentId !== null && x.parentId !== undefined);
    let objects = quotaObjectsList.filter(x => x.parentId === null || x.parentId === undefined).map((x) => {
        let childrenQuotaObject = quotasObjectChildren.find(y => x.shiftLineId === y.parentId);

        if (childrenQuotaObject !== null && childrenQuotaObject !== undefined) {
            if (x.children) {
                x.children.push({
                    id: `${x.shiftLineId}-${childrenQuotaObject.parentId}`,
                    ...childrenQuotaObject,
                    type: 'zone'
                })
                return {
                    ...x,
                    id: x.shiftLineId
                }
            }
            else {
                return {
                    ...x,
                    id: x.shiftLineId,
                    children: [{
                        id: `${x.shiftLineId}-${childrenQuotaObject.parentId}`,
                        ...childrenQuotaObject,
                        type: 'zone'
                    }]
                }
            }
        }
        return { ...x }
    })

    for (const key in objects) {

        quotas.push({ ...objects[key], type: 'zone' });

    }

    if (quotas.length > 0) {
        quotas[0].firstQuota = true;
    }

    quotas = sortBy(quotas, ['sort']);

    return quotas;
}

export const getShiftLineHeight = (shiftLine, workplan) => {
    let maxItemsByDay = 1;

    for (let i = 1; i <= workplan.nbDays; i++) {
        if (exists(shiftLine['day' + i])) {
            maxItemsByDay = shiftLine['day' + i].length > maxItemsByDay ? shiftLine['day' + i].length : maxItemsByDay;
        }
    }

    return maxItemsByDay;
}

export const evalForeverToNull = (date) => {
    return date === tForEver || date === '3000-01-01' ? null : date;
}

export const evalForeverDateToString = (date, t) => {
    return date === "3000-01-01" ? tForEver : DateFormatService.formatDateOrEmpty(date, UkgDateFormat.LongDate);
}

export const evalForeverStringToDate = (date) => {
    return date === tForEver ? "3000-01-01" : date;
}

export const evalDisabledDateRanges = (segments, actionType, target) => {
    let dateRanges = []
    const disabledSummarySegmentStatuses = [0, 2, 3]
    for (const [index, segment] of segments.entries()) {
        const segmentEndDate = evalForeverStringToDate(segment.endDate);
        if (index === 0 && segments.length > 1) {
            let isDisabled = segment.type === 'owner' // Owners
                || (actionType === 'relief' && segment.type === 'relief' && segment.employee.label !== 'Unassigned')
                || !segment.employee.label // Inactive
            dateRanges.push({
                startDate: segment.startDate,
                endDate: segmentEndDate,
                isDisabled: isDisabled
            })
            continue
        }

        if (!disabledSummarySegmentStatuses.includes(segment.summaryReliefStatus)) {
            if (segment.type === 'relief' && segment.employee.label) {
                if (actionType === 'relief' && segment.employee.label !== 'Unassigned' && segment.id !== target.id) {
                    dateRanges.push({
                        startDate: segment.startDate,
                        endDate: segmentEndDate,
                        isDisabled: true,
                    })
                }
                else {
                    let lastRange = dateRanges[dateRanges.length - 1]
                    if (lastRange && !lastRange.isDisabled) {
                        dateRanges[dateRanges.length - 1] = {
                            ...dateRanges[dateRanges.length - 1],
                            endDate: segmentEndDate,
                        }
                    } else {
                        dateRanges.push({
                            startDate: segment.startDate,
                            endDate: segmentEndDate,
                            isDisabled: false,
                        })
                    }
                }
            } else if (segment.type === 'owner') { // type owner
                let lastRange = dateRanges[dateRanges.length - 1]
                if (lastRange && !lastRange.isDisabled && (target.id === segment.id)) {
                    dateRanges[dateRanges.length - 1] = {
                        ...dateRanges[dateRanges.length - 1],
                        endDate: segmentEndDate,
                    }
                } else {
                    dateRanges.push({
                        startDate: segment.startDate,
                        endDate: segmentEndDate,
                        isDisabled: target.id === segment.id || segment.markedForDeletion ? false : true,
                    })
                }
            }
        }
        else { //inactive
            dateRanges.push({
                startDate: segment.startDate,
                endDate: segmentEndDate,
                isDisabled: true,
            })
        }
    }
    return dateRanges
}

export const scrollIntoView = id => {
    let element = document.getElementById(id);
    element.scrollIntoView({ behavior: "smooth" });
}

export const isStateIndirect = (state) => {
    let isIndirect = false
    if (state.statesDetails !== null) {
        if (moment().isBefore(moment(state.effectiveDate))) {
            isIndirect = state.statesDetails[0].isIndirect;
        } else if (moment().isAfter(moment(state.endDate))) {
            isIndirect = state.statesDetails[state.statesDetails.length - 1].isIndirect;
        } else {
            for (const detail of state.statesDetails) {
                var momentIsBetweenDetailsDates = moment().isBetween(moment.utc(detail.startDate, 'x'), moment.utc(detail.endDate, 'x'), 'd', '[]');
                if (momentIsBetweenDetailsDates) {
                    isIndirect = detail.isIndirect;
                    break;
                } else if (detail.startDate == null) {
                    isIndirect = detail.isIndirect;
                    break;
                }
            }
        }
    }
    return isIndirect
}